import * as THREE from 'three';
import { box, cylinderN, plane } from '@/art/geometry';
import { emissive, surface } from '@/art/materials';

export interface ObjectiveMarkerSnapshot {
  readonly visible: boolean;
  readonly distance: number;
  readonly pulse: number;
}

const MARKER = {
  ringRadius: 1.15,
  ringThickness: 0.06,
  pulseScale: 0.18,
  pinHeight: 1.25,
  hideDistance: 1.8,
  visibleDistance: 240,
} as const;

export class ObjectiveMarker {
  readonly object = new THREE.Group();
  readonly #ring: THREE.Group;
  readonly #pin: THREE.Mesh;
  readonly #glow: THREE.Mesh;
  readonly #target = new THREE.Vector2();
  #pulse = 0;
  #distance = 0;

  constructor(target: THREE.Vector2) {
    this.object.name = 'NA_ObjectiveMarker';
    this.#target.copy(target);

    this.#ring = new THREE.Group();
    this.#ring.name = 'NA_ObjectiveMarker_Ring';
    for (const segment of buildRingSegments()) this.#ring.add(segment);
    this.object.add(this.#ring);

    this.#pin = new THREE.Mesh(box(0.16, MARKER.pinHeight, 0.16), emissive('sodiumAmber', 1.05));
    this.#pin.name = 'NA_ObjectiveMarker_Pin';
    this.#pin.position.y = MARKER.pinHeight / 2;
    this.object.add(this.#pin);

    this.#glow = new THREE.Mesh(plane(1.8, 1.8), emissive('lampGlow', 0.8));
    this.#glow.name = 'NA_ObjectiveMarker_Glow';
    this.#glow.position.y = 0.012;
    this.object.add(this.#glow);

    this.object.position.set(this.#target.x, 0.04, this.#target.y);
  }

  setTarget(target: THREE.Vector2): void {
    this.#target.copy(target);
    this.object.position.set(target.x, 0.04, target.y);
  }

  update(dt: number, playerPosition: THREE.Vector2): void {
    this.#distance = playerPosition.distanceTo(this.#target);
    this.#pulse = (this.#pulse + dt * 1.35) % 1;
    this.object.visible = this.#distance > MARKER.hideDistance && this.#distance < MARKER.visibleDistance;
    if (!this.object.visible) return;

    const wave = Math.sin(this.#pulse * Math.PI * 2) * 0.5 + 0.5;
    const scale = 1 + wave * MARKER.pulseScale;
    this.#ring.scale.setScalar(scale);
    this.#pin.position.y = MARKER.pinHeight / 2 + wave * 0.18;
    this.#glow.scale.setScalar(0.72 + wave * 0.16);
  }

  snapshot(): ObjectiveMarkerSnapshot {
    return {
      visible: this.object.visible,
      distance: this.#distance,
      pulse: this.#pulse,
    };
  }
}

function buildRingSegments(): THREE.Mesh[] {
  const meshes: THREE.Mesh[] = [];
  const material = surface('sodiumAmber');
  const sides = 12;
  for (let i = 0; i < sides; i++) {
    const angle = (i / sides) * Math.PI * 2;
    const segment = new THREE.Mesh(cylinderN(MARKER.ringThickness, 0.18, 8), material);
    segment.name = `NA_ObjectiveMarker_Ring_${i}`;
    segment.position.set(Math.sin(angle) * MARKER.ringRadius, 0.04, Math.cos(angle) * MARKER.ringRadius);
    segment.scale.x = 1.55;
    segment.rotation.z = Math.PI / 2;
    segment.rotation.y = angle;
    meshes.push(segment);
  }
  return meshes;
}
